import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { getHeader } from "../../utils/util";
import { API_ENDPOINT } from "../../utils/constants";

const VoiceFileDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [voiceFile, setVoiceFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Updated status colors for approved / rejected / pending
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "approved":
        return "success";
      case "rejected":
        return "error";
      case "pending":
        return "warning";
      // Backward compatibility with old statuses
      case "completed":
        return "success";
      case "uploading":
        return "warning";
      case "failed":
        return "error";
      default:
        return "warning";
    }
  };

  useEffect(() => {
    const fetchVoiceFile = async () => {
      try {
        const response = await axios.get(
          `${API_ENDPOINT}/voice/voice-files/${id}`,
          getHeader()
        );
        setVoiceFile(response.data.data);
      } catch (err) {
        setError(
          err.response?.data?.message || "Failed to fetch voice file details"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchVoiceFile();
  }, [id]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "400px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 3 }}
        >
          Back to List
        </Button>

        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Voice File Details
        </Typography>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              File Name
            </Typography>
            <Typography variant="body1" fontWeight="medium">
              {voiceFile.fileName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Original File Name
            </Typography>
            <Typography variant="body1">
              {voiceFile.originalFileName}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              User ID
            </Typography>
            <Typography variant="body1">{voiceFile.userId}</Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Prompt Category
            </Typography>
            <Chip
              label={voiceFile.promptCategory}
              size="small"
              sx={{ mt: 0.5 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              File Size
            </Typography>
            <Typography variant="body1">
              {voiceFile.fileSizeReadable}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Status
            </Typography>
            <Chip
              label={voiceFile.status}
              size="small"
              color={getStatusColor(voiceFile.status)}
              variant="filled"
              sx={{ mt: 0.5 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Uploaded At
            </Typography>
            <Typography variant="body1">
              {format(new Date(voiceFile.createdAt), "PPpp")}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Uploaded By
            </Typography>
            <Typography variant="body1">{voiceFile.uploadedBy}</Typography>
          </Grid>

          {voiceFile.errorMessage && (
            <Grid item xs={12}>
              <Typography variant="subtitle2" color="text.secondary">
                Error Message
              </Typography>
              <Alert severity="error" sx={{ mt: 1 }}>
                {voiceFile.errorMessage}
              </Alert>
            </Grid>
          )}
        </Grid>
      </Paper>
    </Box>
  );
};

export default VoiceFileDetails;
